import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Dimensions,
  ScrollView,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';

// Drag Indicator Icon
const DragIndicatorIcon = ({ color = '#3A3A3A', disabled = false }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path 
      d="M9.16659 14.9997C9.16659 15.9163 8.41659 16.6663 7.49992 16.6663C6.58325 16.6663 5.83325 15.9163 5.83325 14.9997C5.83325 14.083 6.58325 13.333 7.49992 13.333C8.41659 13.333 9.16659 14.083 9.16659 14.9997ZM7.49992 8.33301C6.58325 8.33301 5.83325 9.08301 5.83325 9.99967C5.83325 10.9163 6.58325 11.6663 7.49992 11.6663C8.41659 11.6663 9.16659 10.9163 9.16659 9.99967C9.16659 9.08301 8.41659 8.33301 7.49992 8.33301ZM7.49992 3.33301C6.58325 3.33301 5.83325 4.08301 5.83325 4.99967C5.83325 5.91634 6.58325 6.66634 7.49992 6.66634C8.41659 6.66634 9.16659 5.91634 9.16659 4.99967C9.16659 4.08301 8.41659 3.33301 7.49992 3.33301ZM12.4999 6.66634C13.4166 6.66634 14.1666 5.91634 14.1666 4.99967C14.1666 4.08301 13.4166 3.33301 12.4999 3.33301C11.5833 3.33301 10.8333 4.08301 10.8333 4.99967C10.8333 5.91634 11.5833 6.66634 12.4999 6.66634ZM12.4999 8.33301C11.5833 8.33301 10.8333 9.08301 10.8333 9.99967C10.8333 10.9163 11.5833 11.6663 12.4999 11.6663C13.4166 11.6663 14.1666 10.9163 14.1666 9.99967C14.1666 9.08301 13.4166 8.33301 12.4999 8.33301ZM12.4999 13.333C11.5833 13.333 10.8333 14.083 10.8333 14.9997C10.8333 15.9163 11.5833 16.6663 12.4999 16.6663C13.4166 16.6663 14.1666 15.9163 14.1666 14.9997C14.1666 14.083 13.4166 13.333 12.4999 13.333Z" 
      fill={disabled ? '#A0A0A0' : color}
    />
  </Svg>
);

// Toggle Switch Component
interface ToggleSwitchProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ value, onValueChange, disabled = false }) => (
  <TouchableOpacity
    style={styles.toggleContainer}
    onPress={() => !disabled && onValueChange(!value)}
    disabled={disabled}
  >
    <View style={[
      styles.toggleTrack,
      value ? styles.toggleTrackActive : styles.toggleTrackInactive,
      disabled && styles.toggleTrackDisabled
    ]}>
      <View style={[
        styles.toggleThumb,
        value ? styles.toggleThumbActive : styles.toggleThumbInactive
      ]} />
    </View>
  </TouchableOpacity>
);

// Segmented Button Group
interface SegmentedButtonProps {
  options: string[];
  selectedIndex: number;
  onSelectionChange: (index: number) => void;
}

const SegmentedButton: React.FC<SegmentedButtonProps> = ({ options, selectedIndex, onSelectionChange }) => (
  <View style={styles.segmentedContainer}>
    <View style={styles.segmentedWrapper}>
      {options.map((option, index) => (
        <TouchableOpacity
          key={index}
          style={[
            styles.segmentedButton,
            index === selectedIndex && styles.segmentedButtonActive,
            index === 0 && styles.segmentedButtonFirst,
            index === options.length - 1 && styles.segmentedButtonLast,
          ]}
          onPress={() => onSelectionChange(index)}
        >
          <Text style={[
            styles.segmentedButtonText,
            index === selectedIndex && styles.segmentedButtonTextActive
          ]}>
            {option}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  </View>
);

export interface RoleItem {
  id: string;
  name: string;
  enabled: boolean;
  disabled?: boolean;
}

export interface RolesByType {
  DSD: RoleItem[];
  'NON-DSD': RoleItem[];
  Retail: RoleItem[];
  Other: RoleItem[];
}

// Role Item Component
interface RoleItemProps {
  item: RoleItem;
  onToggle: (id: string, enabled: boolean) => void;
}

const RoleItemComponent: React.FC<RoleItemProps> = ({ item, onToggle }) => (
  <View>
    <View style={styles.roleItem}>
      <DragIndicatorIcon disabled={!item.enabled} />
      <View style={styles.roleContent}>
        <Text style={[
          styles.roleLabel,
          !item.enabled && styles.roleLabelDisabled
        ]}>
          {item.name}
        </Text>
      </View>
      <ToggleSwitch
        value={item.enabled}
        onValueChange={(enabled) => onToggle(item.id, enabled)}
        disabled={item.disabled}
      />
    </View>
    <View style={styles.divider} />
  </View>
);

export interface DLOFilterByRoleModalProps {
  visible: boolean;
  onClose: () => void;
  rolesByType: RolesByType;
  onSave: (selectedRoles: RolesByType) => void;
}

const DLOFilterByRoleModal: React.FC<DLOFilterByRoleModalProps> = ({
  visible,
  onClose,
  rolesByType,
  onSave,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<RolesByType>(() => {
    // Initialize with default empty structure or passed prop
    return rolesByType || {
      DSD: [],
      'NON-DSD': [],
      Retail: [],
      Other: [],
    };
  });

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  const isMobile = screenWidth < 768;

  const modalWidth = isMobile ? screenWidth - 32 : Math.min(380, screenWidth - 32);

  // Category mapping for the segmented control
  const categories = ['DSD', 'Non DSD', 'Other'];
  const categoryKeys: (keyof RolesByType)[] = ['DSD', 'NON-DSD', 'Other'];

  // Update selectedRoles when rolesByType prop changes or when modal becomes visible
  useEffect(() => {
    if (rolesByType && Object.keys(rolesByType).length > 0) {
      setSelectedRoles(rolesByType);
    }
  }, [rolesByType]);

  // Reset selectedRoles only when modal opens (visible changes from false to true)
  useEffect(() => {
    if (visible) {
      setSelectedCategory(0); // Reset to first category when modal opens
    }
  }, [visible]);

  const handleRoleToggle = (roleId: string, enabled: boolean) => {
    const currentCategoryKey = categoryKeys[selectedCategory];
    setSelectedRoles(prev => ({
      ...prev,
      [currentCategoryKey]: prev[currentCategoryKey].map(role => 
        role.id === roleId ? { ...role, enabled } : role
      ),
    }));
  };

  const handleSelectAll = (selectAll: boolean) => {
    const currentCategoryKey = categoryKeys[selectedCategory];
    setSelectedRoles(prev => ({
      ...prev,
      [currentCategoryKey]: prev[currentCategoryKey].map(role => 
        ({ ...role, enabled: selectAll })
      ),
    }));
  };

  const areAllRolesSelected = (): boolean => {
    const currentCategoryKey = categoryKeys[selectedCategory];
    const currentRoles = selectedRoles[currentCategoryKey] || [];
    if (currentRoles.length === 0) return false;
    return currentRoles.every(role => role.enabled);
  };

  const handleSave = () => {
    onSave(selectedRoles);
    onClose();
  };

  // Reset to original state on close
  const handleClose = () => {
    setSelectedRoles(rolesByType);
    onClose();
  };

  // Get current roles for selected category
  const getCurrentRoles = (): RoleItem[] => {
    const currentCategoryKey = categoryKeys[selectedCategory];
    const currentRoles = selectedRoles[currentCategoryKey] || [];
    
    return currentRoles;
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <View 
        style={styles.overlay}
      >
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={[
              styles.modalContent,
              { 
                width: modalWidth,
                maxHeight: screenHeight - 100,
                minHeight: 500,
              }
            ]}
            onPress={(e) => e.stopPropagation()}
            activeOpacity={1}
          >
            {/* Header with Segmented Control */}
            <View style={styles.header}>
              <SegmentedButton
                options={categories}
                selectedIndex={selectedCategory}
                onSelectionChange={setSelectedCategory}
              />
            </View>

            {/* Title with Select All */}
            <View style={styles.titleWrapper}>
              <Text style={styles.title}>Edit Role</Text>
              <View style={styles.selectAllToggleContainer}>
                <ToggleSwitch
                  value={areAllRolesSelected()}
                  onValueChange={handleSelectAll}
                />
              </View>
            </View>

            {/* Role List */}
            <View style={styles.listContainer}>
              <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                {getCurrentRoles().map((role) => (
                  <RoleItemComponent
                    key={role.id}
                    item={role}
                    onToggle={handleRoleToggle}
                  />
                ))}
                {getCurrentRoles().length === 0 && (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      No roles available for {categories[selectedCategory]}
                    </Text>
                  </View>
                )}
              </ScrollView>
            </View>

            {/* Sticky Footer */}
            <View style={styles.stickyFooter}>
              <View style={styles.buttonGroup}>
                <View style={styles.spacer} />
                <View style={styles.buttonHierarchy}>
                  <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
                    <Text style={styles.saveButtonText}>Save</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: '#FBFCFE',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    shadowColor: 'rgba(58, 58, 58, 0.08)',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
    minWidth: 320,
    maxWidth: 460,
    position: 'relative',
    paddingHorizontal: 12,
    paddingTop: 24,
    paddingBottom: 60,
    flex: 1,
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px 0 rgba(58, 58, 58, 0.08)',
      },
    }),
  },
  header: {
    marginBottom: 12,
  },
  segmentedContainer: {
    width: '100%',
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  segmentedWrapper: {
    flexDirection: 'row',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#D7E5F8',
    overflow: 'hidden',
  },
  segmentedButton: {
    minWidth: 32,
    minHeight: 32,
    maxHeight: 32,
    paddingHorizontal: 8,
    paddingVertical: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FBFCFE',
  },
  segmentedButtonActive: {
    backgroundColor: '#005CBC',
  },
  segmentedButtonFirst: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  segmentedButtonLast: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  segmentedButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#005CBC',
    paddingHorizontal: 4,
  },
  segmentedButtonTextActive: {
    fontWeight: '700',
    color: '#FFF',
  },
  title: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 28,
    color: '#000',
    marginTop: 12,
    textAlign: 'center',
  },
  titleWrapper: {
    position: 'relative',
    marginTop: 12,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectAllToggleContainer: {
    position: 'absolute',
    right: 8,
    top: 0,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 16,
    paddingHorizontal: 8,
  },
  listContainer: {
    flex: 1,
    minHeight: 200,
    maxHeight: 344,
    marginVertical: 0,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  roleItem: {
    flexDirection: 'row',
    minHeight: 48,
    paddingVertical: 12,
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    borderRadius: 8,
  },
  roleContent: {
    flex: 1,
    paddingRight: 8,
    gap: 2,
  },
  roleLabel: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#3A3A3A',
  },
  roleLabelDisabled: {
    color: '#A0A0A0',
  },
  divider: {
    height: 1,
    backgroundColor: '#D7E5F8',
    marginVertical: 0,
  },
  toggleContainer: {
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  toggleTrack: {
    width: 32,
    height: 16,
    padding: 3,
    borderRadius: 24,
    justifyContent: 'center',
  },
  toggleTrackActive: {
    backgroundColor: '#005CBC',
    alignItems: 'flex-end',
  },
  toggleTrackInactive: {
    backgroundColor: '#616161',
    alignItems: 'flex-start',
  },
  toggleTrackDisabled: {
    backgroundColor: '#616161',
  },
  toggleThumb: {
    width: 10,
    height: 10,
    borderRadius: 999,
    backgroundColor: '#FFF',
  },
  toggleThumbActive: {
    // Additional styling if needed
  },
  toggleThumbInactive: {
    // Additional styling if needed
  },
  stickyFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    maxHeight: 120,
    paddingHorizontal: 12,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
    ...Platform.select({
      web: {
        boxShadow: '0 0 24px 0 rgba(0, 92, 188, 0.08)',
      },
    }),
  },
  buttonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  },
  spacer: {
    width: 47,
    height: 32,
  },
  buttonHierarchy: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
  },
  cancelButton: {
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
  },
  cancelButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    color: '#005CBC',
    maxWidth: 150,
  },
  saveButton: {
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 24,
    backgroundColor: '#005CBC',
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  saveButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    color: '#FFF',
    maxWidth: 150,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    color: '#888888',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
  },
});

export default DLOFilterByRoleModal;
